import type { InterviewDetail, Scorecard, Turn } from './api';

/**
 * Pure helpers for the candidate replay: stitching per-turn clips into one timeline, placing markers,
 * and finding the turn under the playhead. All times are milliseconds from the start of the interview
 * (the same origin as Turn.offsetMs).
 */

export interface ClipSegment {
  seq: number;
  src: string;
  start: number;
  end: number;
}

const DEFAULT_TURN_MS = 4_000;

/** Estimated length of a turn: its measured duration, else the gap to the next turn, else a default. */
function turnLength(turns: Turn[], i: number): number {
  const t = turns[i]!;
  if (t.durationMs && t.durationMs > 0) return t.durationMs;
  const next = turns[i + 1];
  if (next && next.offsetMs > t.offsetMs) return next.offsetMs - t.offsetMs;
  return DEFAULT_TURN_MS;
}

/** One segment per turn that has an audio clip, in time order, never overlapping. */
export function buildClipSegments(turns: Turn[]): ClipSegment[] {
  const sorted = [...turns].sort((a, b) => a.offsetMs - b.offsetMs || a.seq - b.seq);
  const segs: ClipSegment[] = [];
  sorted.forEach((t, i) => {
    if (!t.audioUrl) return;
    const prevEnd = segs.at(-1)?.end ?? 0;
    const start = Math.max(t.offsetMs, prevEnd);
    segs.push({ seq: t.seq, src: t.audioUrl, start, end: start + turnLength(sorted, i) });
  });
  return segs;
}

/** Where time `t` falls: inside a segment, or (in a gap) at the start of the next one. Null past the end. */
export function locateSegment(segs: ClipSegment[], t: number): { index: number; withinMs: number } | null {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i]!;
    if (t < s.start) return { index: i, withinMs: 0 };
    if (t < s.end) return { index: i, withinMs: t - s.start };
  }
  return null;
}

/** Length of the whole timeline from whatever is known. */
export function timelineDuration(interview: Pick<InterviewDetail, 'turns' | 'startedAt' | 'endedAt'>, mediaMs?: number | null): number {
  if (mediaMs && Number.isFinite(mediaMs) && mediaMs > 0) return mediaMs;
  const turns = [...interview.turns].sort((a, b) => a.offsetMs - b.offsetMs);
  let end = 0;
  turns.forEach((t, i) => (end = Math.max(end, t.offsetMs + turnLength(turns, i))));
  for (const s of buildClipSegments(interview.turns)) end = Math.max(end, s.end);
  if (interview.startedAt && interview.endedAt) {
    end = Math.max(end, new Date(interview.endedAt).getTime() - new Date(interview.startedAt).getTime());
  }
  return end;
}

/** The turn being spoken at time `t`: the last turn that started at or before it. */
export function activeTurnSeq(turns: Turn[], t: number): number | null {
  let best: Turn | null = null;
  for (const turn of turns) {
    if (turn.offsetMs <= t && (!best || turn.offsetMs >= best.offsetMs)) best = turn;
  }
  return best?.seq ?? null;
}

export function turnOffset(turns: Turn[], seq: number): number | null {
  return turns.find((t) => t.seq === seq)?.offsetMs ?? null;
}

export type MarkerKind = 'consent' | 'evidence' | 'flag' | 'concern';

export interface Marker {
  kind: MarkerKind;
  atMs: number;
  label: string;
  seq: number | null;
}

/**
 * Markers for the replay timeline.
 * - consent: consentAt relative to startedAt, else the first thing the candidate said;
 * - evidence: each competency evidence quote, at its turn;
 * - flag / concern: failed hard / soft knockout rules, at the turn the fact was established.
 * Markers past `durationMs` are clamped to it; duplicates at the same turn and kind are merged.
 */
export function computeMarkers(
  interview: Pick<InterviewDetail, 'turns' | 'consentAt' | 'startedAt'>,
  card: Pick<Scorecard, 'assessment' | 'knockouts'> | null,
  durationMs: number,
): Marker[] {
  const out: Marker[] = [];
  const clamp = (ms: number) => Math.max(0, Math.min(durationMs > 0 ? durationMs : ms, ms));
  const turns = interview.turns;

  let consentMs: number | null = null;
  if (interview.consentAt && interview.startedAt) {
    const d = new Date(interview.consentAt).getTime() - new Date(interview.startedAt).getTime();
    if (Number.isFinite(d) && d >= 0) consentMs = d;
  }
  if (consentMs === null) {
    const first = [...turns].filter((t) => t.speaker === 'CANDIDATE').sort((a, b) => a.offsetMs - b.offsetMs)[0];
    if (first) consentMs = first.offsetMs;
  }
  if (consentMs !== null) out.push({ kind: 'consent', atMs: clamp(consentMs), label: 'Consent', seq: null });

  if (card) {
    const seen = new Set<string>();
    // A first interview's competencies, or a skills assessment's tasks: both carry evidence.
    const skills = (card.assessment as { tasks?: Array<{ evidence?: Array<{ quote: string; turn: number }> }> }).tasks ?? [];
    for (const comp of [...(card.assessment.competencies ?? []), ...skills]) {
      for (const e of comp.evidence ?? []) {
        const at = turnOffset(turns, e.turn);
        const key = `evidence:${e.turn}`;
        if (at === null || seen.has(key)) continue;
        seen.add(key);
        out.push({ kind: 'evidence', atMs: clamp(at), label: `“${e.quote}”`, seq: e.turn });
      }
    }
    const factTurn = new Map((card.assessment.facts ?? []).map((f) => [f.key, f.turn] as const));
    for (const k of card.knockouts ?? []) {
      if (k.outcome !== 'failed') continue;
      const seq = factTurn.get(k.fact);
      if (seq == null) continue;
      const at = turnOffset(turns, seq);
      const kind: MarkerKind = k.severity === 'hard' ? 'flag' : 'concern';
      const key = `${kind}:${seq}`;
      if (at === null || seen.has(key)) continue;
      seen.add(key);
      out.push({ kind, atMs: clamp(at), label: k.message, seq });
    }
  }
  return out.sort((a, b) => a.atMs - b.atMs);
}
